import { ChevronDown, Loader2, Send } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { replyToTicket } from '@/api/member';
import TicketThread from '@/components/support/TicketThread';

// What each stored status means to the member who opened the ticket.
const STATUS = {
  Open: { label: 'Awaiting Support', color: '#FFC20E', border: 'rgba(255,194,14,0.4)', bg: 'rgba(255,194,14,0.12)' },
  Pending: { label: 'Support Replied', color: '#9bb8ff', border: 'rgba(155,184,255,0.4)', bg: 'rgba(155,184,255,0.12)' },
  Resolved: { label: 'Resolved', color: '#7dd87a', border: 'rgba(125,216,122,0.4)', bg: 'rgba(125,216,122,0.10)' },
};

/** One of the member's tickets: the summary row, and when opened its conversation plus a reply box. */
const MemberTicketItem = ({ ticket, onReplied }) => {
  const uid = useId();
  const [expanded, setExpanded] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const st = STATUS[ticket.status] || STATUS.Open;

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await replyToTicket(ticket.ticket_id, reply.trim());
      toast.success('Reply sent');
      setReply('');
      onReplied();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Reply failed: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <li className="py-4 px-1" data-testid={`ticket-${ticket.ticket_id.slice(0, 6)}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 flex-wrap text-left"
        data-testid={`ticket-toggle-${ticket.ticket_id.slice(0, 6)}`}
      >
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] font-semibold border"
          style={{ color: st.color, borderColor: st.border, background: st.bg }}
        >
          {st.label}
        </span>
        <h4 className="font-semibold text-[15px] truncate flex-1 min-w-0">{ticket.subject}</h4>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
          {String(ticket.last_message_at || ticket.created_at)
            .replace('T', ' ')
            .slice(0, 16)}
        </span>
        <ChevronDown className={`w-4 h-4 text-[var(--sc-text-mute)] transition ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded ? (
        <div className="mt-4 space-y-4">
          <TicketThread messages={ticket.messages} viewer="member" />
          <form onSubmit={send} className="space-y-2">
            <label htmlFor={`${uid}-reply`} className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">
              {ticket.status === 'Resolved' ? 'Still Need Help? Reply To Reopen' : 'Reply'}
            </label>
            <textarea
              id={`${uid}-reply`}
              rows={3}
              maxLength={4000}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write a reply to Support…"
              className="w-full px-4 py-3 rounded-xl bg-[rgba(17,17,17,0.6)] border border-[rgba(255,182,18,0.20)] text-[var(--sc-text)] placeholder:text-[var(--sc-text-mute)] focus:outline-none focus:border-[rgba(255,182,18,0.55)] resize-y"
              data-testid="ticket-reply"
            />
            <div className="flex justify-end">
              <button type="submit" disabled={sending || !reply.trim()} className="sc-btn-white" data-testid="ticket-reply-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Reply
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </li>
  );
};

export default MemberTicketItem;
